import { SxProps, Theme, useTheme } from "@mui/material";
import {
  DataGridPremiumProps,
  GridGroupNode,
  GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  ProductionBasketsGrid,
  ProductionBasketRow,
} from "./ProductionBasketsGrid";

export type GroupedProductionBasketRow = ProductionBasketRow & {
  productGroupId: number;
  productGroupName: string;
};

const rowGroupingModel: DataGridPremiumProps["rowGroupingModel"] = [
  "productGroupName",
];

const getRowClassName = (params: { id: string | number }): string => {
  if (typeof params.id === "string" && params.id.startsWith("auto-generated")) {
    return "product-group-row";
  }
  return "";
};

type GroupedProductionBasketsGridProps<
  TRow extends GroupedProductionBasketRow,
> = Omit<
  Parameters<typeof ProductionBasketsGrid<TRow>>[0],
  | "rowGroupingModel"
  | "groupingColDef"
  | "isGroupExpandedByDefault"
  | "getRowClassName"
  | "initialState"
  | "apiRef"
> & {
  defaultExpanded?: boolean;
};

export const GroupedProductionBasketsGrid = <
  TRow extends GroupedProductionBasketRow,
>({
  sx,
  defaultExpanded = true,
  ...props
}: GroupedProductionBasketsGridProps<TRow>) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const apiRef = useGridApiRef();

  // Track which rows have been explicitly expanded/collapsed
  const expansionStateRef = useRef(new Map<GridRowId, boolean>());

  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode) => {
      const storedState = expansionStateRef.current.get(node.id);
      // If we've seen this row before, use the stored state
      if (storedState !== undefined) {
        return storedState;
      }
      // Otherwise use the default
      return defaultExpanded;
    },
    [defaultExpanded]
  );

  useEffect(() => {
    if (!apiRef.current) return;
    const unsubscribe = apiRef.current.subscribeEvent(
      "rowExpansionChange",
      (node: GridGroupNode) => {
        // When a row expansion changes, store the new state
        const isExpanded = node.childrenExpanded ?? false;
        expansionStateRef.current.set(node.id, isExpanded);
      }
    );
    return unsubscribe;
  }, [apiRef]);

  const groupingColDef: DataGridPremiumProps<TRow>["groupingColDef"] = useMemo(
    () => ({
      headerName: t("productGroup"),
      width: 200,
      hideDescendantCount: true,
    }),
    [t]
  );

  const initialState: DataGridPremiumProps["initialState"] = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          productGroupName: false,
        },
      },
    }),
    []
  );

  const groupRowStyles = {
    "& .product-group-row": {
      backgroundColor: theme.palette.action.hover,
      fontWeight: "bold",
      "& .MuiDataGrid-cell": {
        fontWeight: "bold",
      },
    },
    "& .product-group-row:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  } as const;

  const mergedSx: SxProps<Theme> = [groupRowStyles].concat(
    sx ? (Array.isArray(sx) ? sx : [sx]) : []
  );

  return (
    <ProductionBasketsGrid
      {...props}
      apiRef={apiRef}
      initialState={initialState}
      rowGroupingModel={rowGroupingModel}
      groupingColDef={groupingColDef}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      getRowClassName={getRowClassName}
      sx={mergedSx}
    />
  );
};
